/**
 * SMS length and segment accounting, following GSM 03.38.
 *
 * Operators bill per segment, not per message, so this is what decides how much
 * a store pays for a single send. Both the API (which persists
 * `BaseMessage.numSmsSegments`) and the CRM composer preview run this exact
 * function, so what the mechanic is shown can never drift from what is billed.
 */

export enum SmsEncoding {
  GSM7 = "GSM7",
  UCS2 = "UCS2",
}

/**
 * Characters encodable in one septet. Deliberately spelled out rather than
 * expressed as ranges: the GSM alphabet is not contiguous in Unicode, and the
 * Swedish letters that matter most here (å ä ö é ü) sit in it while visually
 * similar ones do not.
 */
const GSM_BASIC_CHARSET = new Set(
  [
    "@£$¥èéùìòÇ",
    "\nØø\rÅå",
    "Δ_ΦΓΛΩΠΨΣΘΞ",
    "ÆæßÉ",
    " !\"#¤%&'()*+,-./",
    "0123456789:;<=>?",
    "¡ABCDEFGHIJKLMNO",
    "PQRSTUVWXYZÄÖÑÜ§",
    "¿abcdefghijklmno",
    "pqrstuvwxyzäöñüà",
  ]
    .join("")
    .split(""),
)

/**
 * Encodable in GSM-7, but only as an escape sequence, so each one costs two
 * septets rather than one.
 */
const GSM_EXTENDED_CHARSET = new Set([
  "^",
  "{",
  "}",
  "\\",
  "[",
  "~",
  "]",
  "|",
  "€",
])

const GSM7_SINGLE_CAPACITY = 160
const GSM7_CONCATENATED_CAPACITY = 153
const UCS2_SINGLE_CAPACITY = 70
const UCS2_CONCATENATED_CAPACITY = 67

export interface SmsChar {
  /** The character itself. May be a surrogate pair, so not always length 1. */
  char: string
  /** Units this character consumes: 2 for GSM escapes and surrogate pairs. */
  cost: number
  /** False for anything outside GSM-7, which forces the whole message to UCS-2. */
  isGsm: boolean
}

export interface SmsSegmentInfo {
  encoding: SmsEncoding
  /** Total units consumed: septets for GSM-7, UTF-16 code units for UCS-2. */
  totalCount: number
  numSegments: number
  gsmCharCount: number
  unicodeCharCount: number
  /** Units still free in the final segment. */
  remainingInLastSegment: number
  /** Capacity of each segment at the current segment count. */
  segmentCapacity: number
  /**
   * What a message may hold before it needs a second segment, i.e. before the
   * concatenation header starts costing 7 units per part. Distinct from
   * segmentCapacity, which already reports the reduced 153/67 once there is a
   * second part — so this is the one to subtract from totalCount to tell a
   * mechanic how much to trim to get back down to a single SMS.
   */
  singleSegmentCapacity: number
  /** Per-segment breakdown, for the character grid in the CRM. */
  parts: SmsChar[][]
}

/**
 * Splits into user-perceived characters rather than UTF-16 code units, so an
 * emoji stays one cell in the grid even though it costs two units.
 */
const toCharacters = (message: string): string[] => Array.from(message)

const describeCharacter = (char: string): SmsChar => {
  if (GSM_BASIC_CHARSET.has(char)) return { char, cost: 1, isGsm: true }
  if (GSM_EXTENDED_CHARSET.has(char)) return { char, cost: 2, isGsm: true }

  // Outside GSM-7 entirely. Cost is in UTF-16 code units, which is 2 for
  // anything above the BMP (emoji), 1 otherwise.
  return { char, cost: char.length, isGsm: false }
}

/**
 * Fills segments left to right without ever splitting a character across a
 * boundary. A GSM escape pair or a surrogate pair that would straddle the end
 * of a segment moves whole into the next one, which is why the last segment can
 * report one unit of slack that nothing can use.
 */
const splitIntoParts = (
  characters: SmsChar[],
  capacity: number,
): SmsChar[][] => {
  const parts: SmsChar[][] = []
  let current: SmsChar[] = []
  let used = 0

  for (const character of characters) {
    if (used + character.cost > capacity) {
      parts.push(current)
      current = []
      used = 0
    }

    current.push(character)
    used += character.cost
  }

  parts.push(current)

  return parts
}

const sumCost = (characters: SmsChar[]): number =>
  characters.reduce((total, character) => total + character.cost, 0)

export const getSmsSegmentInfo = (message: string): SmsSegmentInfo => {
  // The GSM alphabet above is spelled out in precomposed form, so a decomposed
  // "ä" (a + U+0308, which is what pasting out of some macOS apps produces)
  // would be read as two non-GSM characters and drag the whole message into the
  // Unicode budget — halving capacity and roughly doubling what the store is
  // billed, for text that looks identical on screen.
  const characters = toCharacters(message.normalize("NFC")).map(
    describeCharacter,
  )

  const isGsm7 = characters.every((character) => character.isGsm)
  const encoding = isGsm7 ? SmsEncoding.GSM7 : SmsEncoding.UCS2

  const singleCapacity = isGsm7 ? GSM7_SINGLE_CAPACITY : UCS2_SINGLE_CAPACITY
  const concatenatedCapacity = isGsm7
    ? GSM7_CONCATENATED_CAPACITY
    : UCS2_CONCATENATED_CAPACITY

  const totalCount = sumCost(characters)

  // A concatenated message spends 7 units per part on the UDH, so the capacity
  // only drops once the message actually needs a second part. Measuring against
  // the smaller capacity up front would report 2 segments for a 160-character
  // message the operator bills as 1.
  const fitsInOne = totalCount <= singleCapacity
  const segmentCapacity = fitsInOne ? singleCapacity : concatenatedCapacity

  const parts = fitsInOne
    ? [characters]
    : splitIntoParts(characters, concatenatedCapacity)

  const lastPart = parts[parts.length - 1] ?? []

  return {
    encoding,
    totalCount,
    numSegments: parts.length,
    gsmCharCount: characters.filter((character) => character.isGsm).length,
    unicodeCharCount: characters.filter((character) => !character.isGsm).length,
    remainingInLastSegment: segmentCapacity - sumCost(lastPart),
    segmentCapacity,
    singleSegmentCapacity: singleCapacity,
    parts,
  }
}

export const getNumSmsSegments = (message: string): number =>
  getSmsSegmentInfo(message).numSegments

export interface SmsTrimAdvice {
  /** Distinct characters forcing the message out of GSM-7, in first-seen order. */
  costlyCharacters: string[]
  /** Segments the message would cost with those characters removed. */
  segmentsWithoutCostlyCharacters: number
  /** Units to remove for the message to cost one segment fewer. */
  unitsToRemoveForOneFewerSegment: number
}

/**
 * Works out the cheapest way to make a message cost one SMS less, so the UI can
 * name a concrete action rather than an encoding.
 *
 * A single emoji or a smart quote pasted out of a document drags the whole
 * message from GSM-7 into UCS-2, which cuts capacity from 160 to 70. When that
 * has happened, "remove this character" is a far better instruction than
 * "remove 93 characters" — same saving, a fraction of the editing — so the
 * offending characters are reported by value and the caller can lead with them.
 */
export const getSmsTrimAdvice = (message: string): SmsTrimAdvice => {
  // Matched against parts[], which getSmsSegmentInfo has already normalised.
  const normalized = message.normalize("NFC")
  const {
    totalCount,
    numSegments,
    segmentCapacity,
    singleSegmentCapacity,
    parts,
  } = getSmsSegmentInfo(normalized)

  const costlyCharacters: string[] = []
  for (const { char, isGsm } of parts.flat()) {
    if (!isGsm && !costlyCharacters.includes(char)) costlyCharacters.push(char)
  }

  const withoutCostlyCharacters = Array.from(normalized)
    .filter((char) => !costlyCharacters.includes(char))
    .join("")

  // Dropping to a single segment is not the same arithmetic as dropping to any
  // other count: a lone SMS carries no concatenation header, so it holds the
  // full 160/70 rather than 153/67. Ignoring that under-reports the saving by
  // seven units on the 2 -> 1 step, which is the step mechanics hit most.
  const targetSegments = numSegments - 1
  // Nothing to save on a message that already costs a single segment.
  if (targetSegments < 1) {
    return {
      costlyCharacters,
      segmentsWithoutCostlyCharacters: numSegments,
      unitsToRemoveForOneFewerSegment: 0,
    }
  }

  const targetCapacity =
    targetSegments === 1
      ? singleSegmentCapacity
      : targetSegments * segmentCapacity

  return {
    costlyCharacters,
    segmentsWithoutCostlyCharacters: getNumSmsSegments(withoutCostlyCharacters),
    unitsToRemoveForOneFewerSegment: Math.max(0, totalCount - targetCapacity),
  }
}
